import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { getUserById } from "@/features/users/services/users";

import type { User } from "../schemas/user";

// Query keys for current user
export const currentUserKeys = {
  current: ["currentUser"] as const,
};

/**
 * Hook to get the current authenticated user from Firestore
 * This hook combines Firebase Auth user with Firestore user data
 *
 * @returns Query result with the current user data from Firestore
 *
 * @example
 * ```typescript
 * const { data: currentUser, isLoading, error } = useCurrentUser();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * if (currentUser) {
 *   console.log('Current user:', currentUser.name, currentUser.type);
 * }
 * ```
 */
export function useCurrentUser() {
  const { currentUser: authUser } = useAuth();

  return useQuery({
    queryKey: currentUserKeys.current,
    queryFn: () => {
      if (!authUser?.uid) {
        throw new Error("No authenticated user found");
      }
      return getUserById(authUser.uid);
    },
    enabled: !!authUser?.uid,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: (failureCount, error) => {
      // Don't retry if user doesn't exist in Firestore
      if (error instanceof Error && error.message.includes("not found")) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get the current user with a specific type guard
 * Useful when you need to ensure the user has a specific type
 *
 * @param typeGuard - Function to check if user matches expected type
 * @returns Query result with typed user data
 *
 * @example
 * ```typescript
 * const { data: adminUser } = useCurrentUserWithType(
 *   (user) => user?.type === "admin"
 * );
 *
 * if (adminUser) {
 *   console.log('Admin user:', adminUser.name);
 * }
 * ```
 */
export function useCurrentUserWithType<T extends User>(
  typeGuard: (user: User | null) => user is T
) {
  const { data: user, ...queryResult } = useCurrentUser();

  return {
    ...queryResult,
    data: typeGuard(user ?? null) ? user : null,
  };
}
